import { StowageRulesOutputs } from '../../../types/ViewModel/Stowage/StowageRule/StowageRulesOutputs';
import { StowageRulesInputs } from 'src/types/ViewModel/Stowage/StowageRule/StowageRulesInputs';
import { StowageRuleContext } from '../../../types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { BooleanOperator } from 'src/enums/stowage/stowageRule/booleanOperator';
import { PropertyManager } from './propertyManager';
import { LeafManager } from './leafManager';
import { StowageRuleTestNodeBM } from 'src/types/BusinessModel/Stowage/StowageRule/StowageRuleTestNodeBM';
import { StowageRuleBM } from 'src/types/BusinessModel/Stowage/StowageRule/StowageRuleBM';
import { processConsequence } from '../violations/violationsUtils';
import { StowageRulesOptions } from './stowagePlanRulesManager';
import { getStowageRules } from './stowageRulesUtils';
import { StowageRulesCheckType } from 'src/enums/stowage/stowageRule/stowageRulesCheckType';
import { StowageDataAccessor } from './stowageDataAccessor';

export class StowageRulesManager {
    private leafManager: LeafManager;
    private options: StowageRulesOptions;

    constructor(options: StowageRulesOptions, dataAccessor: StowageDataAccessor) {
        this.options = options;
        const propertyManager = new PropertyManager();
        this.leafManager = new LeafManager(dataAccessor, propertyManager);
    }
    public checkStowageRules(stowageRulesInputs: StowageRulesInputs, stowageRulesOutputs: StowageRulesOutputs): void {
        const defaultContext = this.getDefaultStowageRuleContext(stowageRulesInputs);
        const checkType = stowageRulesInputs.stowageRulesCheckType;
        const shouldIncludeSoftRules = !this.options.excludeSoftRuleViolations;

        if (checkType === StowageRulesCheckType.Cargo || checkType === StowageRulesCheckType.CargoOperation) {
            const stowageRuleContext = { ...defaultContext };
            const allRules = [
                ...(stowageRuleContext.cargo.product.category.stowageRules
                    ? getStowageRules(stowageRuleContext.cargo.product.category.stowageRules, shouldIncludeSoftRules, true)
                    : []),
                ...(stowageRuleContext.cargo.product.stowageRules
                    ? getStowageRules(stowageRuleContext.cargo.product.stowageRules, shouldIncludeSoftRules, true)
                    : []),
                ...(stowageRulesInputs.stowageRules || []),
            ];

            for (const rule of allRules) {
                this.checkStowageRule(rule, stowageRuleContext, stowageRulesOutputs);
            }
            return;
        }

        if (checkType === StowageRulesCheckType.CargoOperationTransit) {
            const stowageRuleContext = { ...defaultContext };
            // For transit operations, only check explicitly passed rules (e.g., USCG compatibility)
            // Do not include product/category rules to avoid duplicate violations at each port
            const allRules = stowageRulesInputs.stowageRules || [];

            for (const rule of allRules) {
                this.checkStowageRule(rule, stowageRuleContext, stowageRulesOutputs);
            }
            return;
        }

        if (checkType === StowageRulesCheckType.Rotation) {
            const cargoContents = stowageRulesInputs.tankContent?.cargoContents;
            if (!cargoContents?.length) return;

            const targetCargoGuid = stowageRulesInputs.cargo?.cargoGuid;
            const stowageRuleContext = { ...defaultContext };

            for (const content of cargoContents) {
                const cargo = content.cargo;
                if (targetCargoGuid && cargo.cargoGuid !== targetCargoGuid) continue;

                stowageRuleContext.cargo = cargo;

                for (const rule of stowageRulesInputs.stowageRules) {
                    this.checkStowageRule(rule, stowageRuleContext, stowageRulesOutputs);
                }
            }
        }
    }

    private getDefaultStowageRuleContext(stowageRulesInputs: StowageRulesInputs): StowageRuleContext {
        return {
            cargoTanks: stowageRulesInputs.cargoTanks,
            tankContent: stowageRulesInputs.tankContent,
            tank: stowageRulesInputs.tank,
            portCall: stowageRulesInputs.portCall,
            berthCall: stowageRulesInputs.berthCall,
            applyConsequenceToAlltanks: false,
            cargo: stowageRulesInputs.cargo,
            cargoOperation: stowageRulesInputs.cargoOperation,
            adjacentTanks: [],
        };
    }

    private checkStowageRule(rule: StowageRuleBM, stowageRuleContext: StowageRuleContext, stowageRulesOutputs: StowageRulesOutputs): void {
        // Reset adjacentTanks array by assigning new empty array instead of modifying existing
        stowageRuleContext.adjacentTanks = [];

        // Process condition and immediately return if false to avoid unnecessary consequence processing
        if (this.processNode(rule.condition, stowageRuleContext)) {
            processConsequence(rule.id, rule.consequence, stowageRuleContext, stowageRulesOutputs);
        }
    }

    private processNode(node: StowageRuleTestNodeBM, stowageRuleContext: StowageRuleContext): boolean {
        // Handle leaf node directly
        if (node.leaf) {
            return this.leafManager.processLeaf(node.leaf, stowageRuleContext);
        }

        // For non-leaf nodes, use short-circuit evaluation
        const isAndOperator = node.operator === BooleanOperator.AND;

        // Use length check to avoid array creation if empty
        if (!node.children?.length) {
            return isAndOperator; // AND returns true for empty set, OR returns false
        }

        // Short-circuit evaluation for AND/OR operations
        return isAndOperator
            ? node.children.every((child) => this.processNode(child, stowageRuleContext))
            : node.children.some((child) => this.processNode(child, stowageRuleContext));
    }
}
